import React, { useState } from 'react';
import { 
  Card, 
  Timeline, 
  Typography, 
  Space, 
  Tag, 
  Button, 
  Drawer, 
  Descriptions, 
  Alert,
  Spin,
  Empty,
  Tooltip,
  Row,
  Col
} from 'antd';
import { 
  HistoryOutlined,
  UserOutlined,
  ClockCircleOutlined,
  ShoppingCartOutlined,
  PercentageOutlined,
  CreditCardOutlined,
  PrinterOutlined,
  RestOutlined,
  CloseCircleOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
  EditOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { useGetOrderEventSummaryQuery, OrderEvent } from '../../services/orderEventsApi';
import { useResponsive } from '../../hooks/useResponsive';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { formatGuests } from '../../utils/guests';

const { Text } = Typography;

interface OrderHistoryProps {
  orderId: string;
  visible: boolean;
  onClose: () => void;
  orderNumber?: string;
  tableNumber?: string;
}

interface EventTypeConfig {
  color: string;
  icon: React.ReactNode;
  label: string;
  description: string;
}

const eventTypeConfigs: Record<string, EventTypeConfig> = {
  OrderCreated: {
    color: '#52c41a',
    icon: <PlusCircleOutlined />,
    label: 'Orden Creada',
    description: 'La orden fue creada e iniciada'
  },
  ItemAdded: {
    color: '#1890ff',
    icon: <ShoppingCartOutlined />,
    label: 'Producto Agregado',
    description: 'Se agregó un producto a la orden'
  },
  ItemCancelled: {
    color: '#ff4d4f',
    icon: <MinusCircleOutlined />,
    label: 'Producto Cancelado',
    description: 'Se canceló un producto de la orden'
  },
  DiscountApplied: {
    color: '#fa8c16',
    icon: <PercentageOutlined />,
    label: 'Descuento Aplicado',
    description: 'Se aplicó un descuento a la orden'
  },
  PreCheckRequested: {
    color: '#722ed1',
    icon: <PrinterOutlined />,
    label: 'Precuenta Solicitada',
    description: 'Se solicitó la precuenta de la mesa'
  },
  TableReopened: {
    color: '#13c2c2',
    icon: <RestOutlined />,
    label: 'Mesa Reabierta',
    description: 'La mesa fue reabierta después de precuenta'
  },
  PaymentProcessed: {
    color: '#52c41a',
    icon: <CreditCardOutlined />,
    label: 'Pago Procesado',
    description: 'Se procesó el pago de la orden'
  },
  OrderClosed: {
    color: 'var(--color-neutral-text)',
    icon: <CloseCircleOutlined />,
    label: 'Orden Cerrada',
    description: 'La orden fue cerrada y finalizada'
  },
  OrderUpdated: {
    color: '#1890ff',
    icon: <EditOutlined />,
    label: 'Orden Modificada',
    description: 'Se realizaron cambios en la orden'
  }
};

// Cambio de comensales (OrderUpdated con change = 'guests'): una orden vieja
// puede partir de 0 y 0
const guestsLabel = (adults?: number, children?: number) =>
  formatGuests(adults, children) || 'Sin comensales';

const OrderHistory: React.FC<OrderHistoryProps> = ({
  orderId,
  visible,
  onClose,
  orderNumber,
  tableNumber
}) => {
  const { isMobile } = useResponsive();
  const { formatInTimeZone } = useRestaurantTimeZone();
  const [selectedEvent, setSelectedEvent] = useState<OrderEvent | null>(null);
  const [eventDetailVisible, setEventDetailVisible] = useState(false);

  const { 
    data: orderSummary, 
    isLoading, 
    error 
  } = useGetOrderEventSummaryQuery(orderId, {
    skip: !visible || !orderId
  });

  // El backend puede no traer estos campos (despliegues desfasados): nunca asumir que existen.
  const events = orderSummary?.events ?? [];
  const eventTypeCounts = Object.entries(orderSummary?.eventTypeCounts ?? {});
  const firstEventTimestamp = orderSummary?.firstEventTimestamp ?? orderSummary?.orderOpenTime ?? null;
  const lastEventTimestamp = orderSummary?.lastEventTimestamp ?? null;

  const formatEventTime = (timestamp: string): string =>
    formatInTimeZone(timestamp, 'DD/MM/YYYY HH:mm:ss');

  const getEventConfig = (eventType: string): EventTypeConfig => {
    return eventTypeConfigs[eventType] || {
      color: 'var(--color-neutral-text)',
      icon: <InfoCircleOutlined />,
      label: eventType,
      description: 'Evento personalizado'
    };
  };

  const parseEventData = (eventDataString: string): any => {
    try {
      return JSON.parse(eventDataString);
    } catch {
      return {};
    }
  };

  const renderEventDetails = (event: OrderEvent) => {
    const eventData = parseEventData(event.eventData);
    const config = getEventConfig(event.eventType);

    return (
      <Descriptions
        title="Detalles del Evento"
        bordered
        size="small"
        column={1}
        labelStyle={{ fontSize: isMobile ? '12px' : '14px' }}
        contentStyle={{ fontSize: isMobile ? '12px' : '14px' }}
      >
        <Descriptions.Item label="Tipo">{config.label}</Descriptions.Item>
        <Descriptions.Item label="Usuario">{event.userName}</Descriptions.Item>
        <Descriptions.Item label="Fecha y Hora">{formatEventTime(event.eventTimestamp)}</Descriptions.Item>
        
        {/* Event-specific details */}
        {event.eventType === 'ItemAdded' && eventData.productName && (
          <>
            <Descriptions.Item label="Producto">{eventData.productName}</Descriptions.Item>
            <Descriptions.Item label="Cantidad">{eventData.quantity}</Descriptions.Item>
            <Descriptions.Item label="Precio Unitario">${eventData.unitPrice?.toFixed(2)}</Descriptions.Item>
            <Descriptions.Item label="Total">${eventData.total?.toFixed(2)}</Descriptions.Item>
            {eventData.notes && (
              <Descriptions.Item label="Notas">{eventData.notes}</Descriptions.Item>
            )}
          </>
        )}
        
        {event.eventType === 'ItemCancelled' && (
          <>
            <Descriptions.Item label="Producto">{eventData.productName}</Descriptions.Item>
            <Descriptions.Item label="Motivo">{eventData.reason}</Descriptions.Item>
            <Descriptions.Item label="Autorizado por">{eventData.authorizedBy}</Descriptions.Item>
            {eventData.notes && (
              <Descriptions.Item label="Notas">{eventData.notes}</Descriptions.Item>
            )}
          </>
        )}
        
        {event.eventType === 'DiscountApplied' && (
          <>
            {eventData.productName && (
              <Descriptions.Item label="Producto">{eventData.productName}</Descriptions.Item>
            )}
            <Descriptions.Item label="Tipo">{eventData.type === 'percentage' ? 'Porcentaje' : 'Monto Fijo'}</Descriptions.Item>
            <Descriptions.Item label="Valor">{eventData.type === 'percentage' ? `${eventData.value}%` : `$${eventData.value?.toFixed(2)}`}</Descriptions.Item>
            <Descriptions.Item label="Motivo">{eventData.reason}</Descriptions.Item>
            <Descriptions.Item label="Monto Descuento">${eventData.discountAmount?.toFixed(2)}</Descriptions.Item>
            <Descriptions.Item label="Autorizado por">{eventData.authorizedBy}</Descriptions.Item>
            {eventData.notes && (
              <Descriptions.Item label="Notas">{eventData.notes}</Descriptions.Item>
            )}
          </>
        )}
        
        {event.eventType === 'PaymentProcessed' && (
          <>
            <Descriptions.Item label="Monto Total">${eventData.totalAmount?.toFixed(2)}</Descriptions.Item>
            <Descriptions.Item label="Propina">${eventData.tip?.toFixed(2) || '0.00'}</Descriptions.Item>
            <Descriptions.Item label="Métodos de Pago">{eventData.paymentMethodsCount} método(s)</Descriptions.Item>
            {eventData.discountAmount > 0 && (
              <Descriptions.Item label="Descuentos Aplicados">${eventData.discountAmount?.toFixed(2)}</Descriptions.Item>
            )}
          </>
        )}

        {event.eventType === 'OrderUpdated' && eventData.change === 'guests' && (
          <>
            <Descriptions.Item label="Comensales antes">
              {guestsLabel(eventData.previousAdults, eventData.previousChildren)}
            </Descriptions.Item>
            <Descriptions.Item label="Comensales ahora">
              {guestsLabel(eventData.adults, eventData.children)}
            </Descriptions.Item>
          </>
        )}
      </Descriptions>
    );
  };

  const handleEventClick = (event: OrderEvent) => {
    setSelectedEvent(event);
    setEventDetailVisible(true);
  };

  if (isLoading) {
    return (
      <Drawer
        title="Historial de Orden"
        placement="right"
        onClose={onClose}
        open={visible}
        width={isMobile ? '100vw' : 600}
        bodyStyle={{ textAlign: 'center', paddingTop: '50px' }}
      >
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>Cargando historial...</div>
      </Drawer>
    );
  }

  if (error) {
    return (
      <Drawer
        title="Historial de Orden"
        placement="right"
        onClose={onClose}
        open={visible}
        width={isMobile ? '100vw' : 600}
      >
        <Alert
          message="Error al cargar historial"
          description="No se pudo cargar el historial de eventos de la orden"
          type="error"
          showIcon
        />
      </Drawer>
    );
  }

  return (
    <>
      <Drawer
        title={
          <Space>
            <HistoryOutlined />
            <span>Historial de Orden</span>
          </Space>
        }
        placement="right"
        onClose={onClose}
        open={visible}
        width={isMobile ? '100vw' : 600}
        bodyStyle={{ padding: isMobile ? '12px' : '24px' }}
      >
        {orderSummary ? (
          <>
            {/* Order Summary Header */}
            <Card 
              size="small" 
              style={{ marginBottom: '16px' }}
              bodyStyle={{ padding: isMobile ? '12px' : '16px' }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>
                    Orden:
                  </Text>
                  <br />
                  <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>
                    #{orderNumber || orderSummary.orderNumber}
                  </Text>
                </Col>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>
                    Mesa:
                  </Text>
                  <br />
                  <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>
                    {tableNumber || orderSummary.tableNumber}
                  </Text>
                </Col>
                <Col span={24} style={{ marginTop: '8px' }}>
                  <Space size="small" wrap>
                    <Tag color="blue">{orderSummary.totalEvents} eventos</Tag>
                    {firstEventTimestamp && (
                      <Tag color="green">
                        Iniciado: {formatEventTime(firstEventTimestamp)}
                      </Tag>
                    )}
                    {lastEventTimestamp && lastEventTimestamp !== firstEventTimestamp && (
                      <Tag color="orange">
                        Último: {formatEventTime(lastEventTimestamp)}
                      </Tag>
                    )}
                  </Space>
                </Col>
              </Row>
            </Card>

            {/* Event Type Summary */}
            {eventTypeCounts.length > 0 && (
              <Card
                title="Resumen de Actividad"
                size="small"
                style={{ marginBottom: '16px' }}
                bodyStyle={{ padding: isMobile ? '12px' : '16px' }}
              >
                <Space size="small" wrap>
                  {eventTypeCounts.map(([eventType, count]) => {
                    const config = getEventConfig(eventType);
                    return (
                      <Tooltip key={eventType} title={config.description}>
                        <Tag color={config.color} style={{ fontSize: isMobile ? '11px' : '12px' }}>
                          {config.icon} {config.label}: {count}
                        </Tag>
                      </Tooltip>
                    );
                  })}
                </Space>
              </Card>
            )}

            {/* Timeline */}
            <Card 
              title="Línea de Tiempo" 
              size="small"
              bodyStyle={{ padding: isMobile ? '12px' : '16px' }}
            >
              {events.length > 0 ? (
                <Timeline
                  mode={isMobile ? 'left' : 'left'}
                  style={{ marginTop: '16px' }}
                >
                  {events.map((event) => {
                    const config = getEventConfig(event.eventType);
                    const eventData = parseEventData(event.eventData);
                    
                    return (
                      <Timeline.Item
                        key={event.id}
                        color={config.color}
                        dot={config.icon}
                      >
                        <div style={{ paddingBottom: '16px' }}>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'flex-start',
                            flexDirection: isMobile ? 'column' : 'row',
                            marginBottom: '8px'
                          }}>
                            <div>
                              <Text strong style={{ fontSize: isMobile ? '13px' : '14px' }}>
                                {config.label}
                              </Text>
                              <br />
                              <Text type="secondary" style={{ fontSize: isMobile ? '11px' : '12px' }}>
                                <UserOutlined style={{ marginRight: '4px' }} />
                                {event.userName}
                              </Text>
                            </div>
                            <div style={{ 
                              textAlign: isMobile ? 'left' : 'right',
                              marginTop: isMobile ? '4px' : '0'
                            }}>
                              <Text type="secondary" style={{ fontSize: isMobile ? '11px' : '12px' }}>
                                <ClockCircleOutlined style={{ marginRight: '4px' }} />
                                {formatEventTime(event.eventTimestamp)}
                              </Text>
                            </div>
                          </div>
                          
                          {/* Event-specific preview */}
                          <div style={{ fontSize: isMobile ? '12px' : '13px', color: 'var(--text-secondary)' }}>
                            {event.eventType === 'ItemAdded' && eventData.productName && (
                              <span>Agregó: {eventData.quantity}x {eventData.productName}</span>
                            )}
                            {event.eventType === 'ItemCancelled' && eventData.productName && (
                              <span>Canceló: {eventData.productName} ({eventData.reason})</span>
                            )}
                            {event.eventType === 'DiscountApplied' && (
                              <span>
                                Descuento: {eventData.type === 'percentage' ? `${eventData.value}%` : `$${eventData.value?.toFixed(2)}`} 
                                {eventData.reason && ` - ${eventData.reason}`}
                                {eventData.productName && ` (${eventData.productName})`}
                              </span>
                            )}
                            {event.eventType === 'PaymentProcessed' && (
                              <span>Pago: ${eventData.totalAmount?.toFixed(2)}</span>
                            )}
                            {event.eventType === 'OrderUpdated' && eventData.change === 'guests' && (
                              <span>
                                Comensales: {(eventData.previousAdults ?? 0) + (eventData.previousChildren ?? 0)}
                                {' → '}
                                {(eventData.adults ?? 0) + (eventData.children ?? 0)}
                              </span>
                            )}
                          </div>
                          
                          <Button 
                            type="link" 
                            size="small" 
                            onClick={() => handleEventClick(event)}
                            style={{ 
                              padding: 0, 
                              height: 'auto',
                              fontSize: isMobile ? '11px' : '12px',
                              marginTop: '4px'
                            }}
                          >
                            Ver detalles
                          </Button>
                        </div>
                      </Timeline.Item>
                    );
                  })}
                </Timeline>
              ) : (
                <Empty 
                  description="No hay eventos registrados"
                  style={{ padding: isMobile ? '20px' : '40px' }}
                />
              )}
            </Card>
          </>
        ) : (
          <Empty 
            description="No se encontró información del historial"
            style={{ padding: isMobile ? '40px 20px' : '80px 40px' }}
          />
        )}
      </Drawer>

      {/* Event Detail Modal */}
      <Drawer
        title="Detalle del Evento"
        placement="right"
        onClose={() => setEventDetailVisible(false)}
        open={eventDetailVisible}
        width={isMobile ? '100vw' : 450}
        bodyStyle={{ padding: isMobile ? '12px' : '24px' }}
      >
        {selectedEvent && renderEventDetails(selectedEvent)}
      </Drawer>
    </>
  );
};

export default OrderHistory;